/* OTTR - full screen viewer for the wall tiles.
 *
 * Reads the tiles that are already on the page rather than asking the
 * Liquid to carry extra data attributes: the markup in blocks/ottr-tile
 * already holds the picture, the chip and the credit, so the viewer can
 * be built from the DOM and no other agent's file needs touching.
 *
 * Opts in per container: any .wall, or anything carrying data-lb-group.
 * A tile with no <img class="shot"> (the "waiting on a post" slots) is
 * skipped - there is nothing to open.
 *
 * TEARDOWN IS NOT LEFT TO THE UA. Closing has to put three things back:
 * the scroll lock, the loaded picture, and keyboard focus. The obvious
 * home for that is the dialog's own `close` event - and in testing that
 * event did not arrive, not even on a plain untouched <dialog> on this
 * page. So teardown() is called directly by our own close(), Escape is
 * intercepted rather than left to the UA, and the `close` listener stays
 * only as a backstop. teardown() is idempotent, so running twice is fine
 * and running never is not possible.
 */
(function () {
  'use strict';
  if (window.__ottrLightbox) return;
  window.__ottrLightbox = true;

  var S = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round">';
  var ICON = {
    zoom: S + '<path d="M9 3H3v6M15 3h6v6M9 21H3v-6M15 21h6v-6"/></svg>',
    x:    S + '<path d="M5 5l14 14M19 5L5 19"/></svg>',
    prev: S + '<path d="M15 4L7 12l8 8"/></svg>',
    next: S + '<path d="M9 4l8 8-8 8"/></svg>'
  };

  var dlg, img, chip, by, note, count, prevB, nextB;
  var list = [], idx = 0, opener = null;

  function txt(el, sel) {
    var n = el.querySelector(sel);
    return n ? (n.textContent || '').trim() : '';
  }

  /* The CDN is asked for 1600px for a tile. Full screen on a retina laptop
     wants more than that, and the URL is the only place to say so. Both
     image_url (?width=) and file_url (&width=) end up with the same key. */
  function big(src) {
    if (!src) return '';
    return /[?&]width=\d+/.test(src) ? src.replace(/([?&]width=)\d+/, '$12400') : src;
  }

  function set(el, s) {
    el.textContent = s;
    el.hidden = !s;
  }

  function teardown() {
    document.documentElement.classList.remove('lb-open');
    if (img) { img.removeAttribute('src'); img.classList.remove('is-load'); }
    if (opener && document.contains(opener)) { try { opener.focus(); } catch (e) {} }
    opener = null;
  }

  function close() {
    if (dlg && dlg.open) {
      try { dlg.close(); } catch (e) { dlg.removeAttribute('open'); }
    }
    teardown();
  }

  function build() {
    dlg = document.createElement('dialog');
    dlg.className = 'ottr-lb';
    dlg.setAttribute('aria-label', 'Full screen view');
    dlg.innerHTML =
      '<figure class="ottr-lb__wrap">' +
        '<div class="ottr-lb__stage"><img class="ottr-lb__img" alt=""></div>' +
        '<figcaption class="ottr-lb__cap">' +
          '<span class="ottr-lb__chip" hidden></span>' +
          '<b class="ottr-lb__by" hidden></b>' +
          '<span class="ottr-lb__n" hidden></span>' +
          '<span class="ottr-lb__note" hidden></span>' +
        '</figcaption>' +
      '</figure>' +
      '<button class="ottr-lb__btn ottr-lb__x" type="button" aria-label="Close">' + ICON.x + '</button>' +
      '<button class="ottr-lb__btn ottr-lb__prev" type="button" aria-label="Previous">' + ICON.prev + '</button>' +
      '<button class="ottr-lb__btn ottr-lb__next" type="button" aria-label="Next">' + ICON.next + '</button>';
    document.body.appendChild(dlg);

    img   = dlg.querySelector('.ottr-lb__img');
    chip  = dlg.querySelector('.ottr-lb__chip');
    by    = dlg.querySelector('.ottr-lb__by');
    note  = dlg.querySelector('.ottr-lb__note');
    count = dlg.querySelector('.ottr-lb__n');
    prevB = dlg.querySelector('.ottr-lb__prev');
    nextB = dlg.querySelector('.ottr-lb__next');

    img.addEventListener('load', function () { img.classList.remove('is-load'); });
    dlg.querySelector('.ottr-lb__x').addEventListener('click', close);
    prevB.addEventListener('click', function () { show(idx - 1); });
    nextB.addEventListener('click', function () { show(idx + 1); });

    /* A full-viewport dialog reports backdrop clicks as clicks on itself,
       and the stage is the empty space around a contained picture. Both
       mean "outside the picture", so both close. */
    dlg.addEventListener('click', function (e) {
      if (e.target === dlg ||
          e.target.classList.contains('ottr-lb__stage') ||
          e.target.classList.contains('ottr-lb__wrap')) close();
    });

    /* Escape: taken over rather than left to the UA, so the one path that
       closes without touching our close() cannot leave the page locked. */
    dlg.addEventListener('cancel', function (e) { e.preventDefault(); close(); });

    dlg.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' || e.key === 'Esc') { e.preventDefault(); close(); return; }
      if (e.key === 'ArrowLeft')  { e.preventDefault(); show(idx - 1); }
      if (e.key === 'ArrowRight') { e.preventDefault(); show(idx + 1); }
    });

    var x0 = null;
    dlg.addEventListener('touchstart', function (e) { x0 = e.changedTouches[0].clientX; }, { passive: true });
    dlg.addEventListener('touchend', function (e) {
      if (x0 === null) return;
      var d = e.changedTouches[0].clientX - x0;
      x0 = null;
      if (Math.abs(d) > 48) show(idx + (d < 0 ? 1 : -1));
    }, { passive: true });

    dlg.addEventListener('close', teardown);
  }

  function show(i) {
    if (!list.length) return;
    idx = (i + list.length) % list.length;
    var t = list[idx];
    var im = t.querySelector('img.shot');
    if (!im) return;

    img.classList.add('is-load');
    img.src = big(im.getAttribute('src'));
    img.alt = im.getAttribute('alt') || '';
    if (img.complete) img.classList.remove('is-load');

    set(chip, txt(t, '.src') || (t.classList.contains('vid') ? 'Film' : ''));
    set(by, txt(t, '.by'));
    set(note, txt(t, '.lbl') || t.getAttribute('data-note') || '');
    set(count, list.length > 1 ? (idx + 1) + ' / ' + list.length : '');

    /* Warm the next one so a swipe or an arrow lands on a picture rather
       than on the loading state. */
    if (list.length > 1) {
      var n = list[(idx + 1) % list.length].querySelector('img.shot');
      if (n) { var p = new Image(); p.src = big(n.getAttribute('src')); }
    }
  }

  function open(tiles, i, from) {
    if (!dlg) build();
    list = tiles;
    opener = from;
    prevB.hidden = nextB.hidden = tiles.length < 2;
    show(i);
    document.documentElement.classList.add('lb-open');
    if (dlg.open) return;
    if (typeof dlg.showModal === 'function') {
      try { dlg.showModal(); } catch (e) { dlg.setAttribute('open', ''); }
    } else {
      dlg.setAttribute('open', '');
    }
  }

  function wire() {
    document.querySelectorAll('.wall, [data-lb-group]').forEach(function (box) {
      var tiles = Array.prototype.slice.call(box.querySelectorAll('.media, .vid'))
        .filter(function (t) { return t.querySelector('img.shot'); });
      if (!tiles.length) return;

      tiles.forEach(function (tile, i) {
        if (tile.hasAttribute('data-lb')) return;
        tile.setAttribute('data-lb', '');
        tile.setAttribute('role', 'button');
        tile.setAttribute('tabindex', '0');
        var who = txt(tile, '.by');
        tile.setAttribute('aria-label', 'Open full screen' + (who ? ', ' + who : ''));

        /* Only claim positioning if nothing else has. Some slots are
           positioned by their section and taking it over squares them off
           or drops them out of their grid cell. */
        if (getComputedStyle(tile).position === 'static') tile.style.position = 'relative';

        var cue = document.createElement('span');
        cue.className = 'lb-cue';
        cue.setAttribute('aria-hidden', 'true');
        cue.innerHTML = ICON.zoom;
        tile.appendChild(cue);

        tile.addEventListener('click', function (e) {
          if (e.target.closest('a')) return;
          e.preventDefault();
          open(tiles, i, tile);
        });
        tile.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
            e.preventDefault();
            open(tiles, i, tile);
          }
        });
      });
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', wire);
  else wire();

  /* The theme editor swaps a section's markup in place, which throws away
     the wiring on the old nodes. */
  document.addEventListener('shopify:section:load', wire);
  document.addEventListener('shopify:block:select', wire);
})();
